/**
 * SearchEmptyState - Renders quick navigation, filters, recent items, and actions
 * when the search query is empty.
 */

import { Briefcase, ArrowRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { QuickNavItem, QuickFilter, QuickAction } from './types'
import { getColorClass, getIcon } from './types'
import { RecentSearchesSection } from './RecentSearchesSection'

interface SearchEmptyStateProps {
  quickNavItems: QuickNavItem[]
  quickFilters: QuickFilter[]
  quickActions: QuickAction[]
  recentJobs: Array<{ id: string; name: string; client?: string; status?: string }>
  recentSearches: string[]
  onClose: () => void
  onSetQuery: (query: string) => void
}

export function SearchEmptyState({
  quickNavItems,
  quickFilters,
  quickActions,
  recentJobs,
  recentSearches,
  onClose,
  onSetQuery,
}: SearchEmptyStateProps) {
  return (
    <div className="p-3">
      {/* Recent Searches — top of palette so operators see prior queries immediately */}
      <RecentSearchesSection
        recentSearches={recentSearches}
        onSelect={onSetQuery}
        alwaysShow
      />

      {/* Quick Navigation */}
      <div className="mb-4">
        <div className="px-1 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Quick Navigation
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {quickNavItems.map((nav) => {
            const Icon = nav.icon
            return (
              <a
                key={nav.id}
                href={nav.href}
                onClick={onClose}
                className={cn(
                  'flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-all',
                  'hover:bg-muted hover:scale-[1.02]',
                  'border border-transparent hover:border-border'
                )}
              >
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', getColorClass(nav.color))}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-medium text-foreground">{nav.label}</div>
                  {nav.description && <div className="text-[10px] text-muted-foreground mt-0.5">{nav.description}</div>}
                </div>
              </a>
            )
          })}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="mb-4">
        <div className="px-1 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Quick Filters
        </div>
        <div className="flex flex-wrap gap-2">
          {quickFilters.map((filter) => {
            const Icon = filter.icon
            return (
              <button
                key={filter.id}
                onClick={() => onSetQuery(filter.query)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                  'bg-muted/70 hover:bg-muted',
                  'text-muted-foreground hover:scale-[1.02]'
                )}
              >
                <Icon className={cn('w-3.5 h-3.5', `text-${filter.color}-500`)} />
                {filter.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Recent Jobs */}
      {recentJobs.length > 0 && (
        <div className="mb-4">
          <div className="px-1 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            Recent Jobs
          </div>
          <div className="space-y-1">
            {recentJobs.slice(0, 4).map((job) => (
              <a
                key={job.id}
                href={`/jobs/${job.id}`}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 px-2 py-2 rounded-lg transition-colors',
                  'hover:bg-muted'
                )}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{job.name}</div>
                  {job.client && <div className="text-xs text-muted-foreground truncate">{job.client}</div>}
                </div>
                {job.status && (
                  <span className={cn(
                    'px-2 py-0.5 text-[10px] font-medium rounded-full',
                    job.status === 'WIP' || job.status === 'Active Works'
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}>
                    {job.status === 'WIP' ? 'Active' : job.status}
                  </span>
                )}
                <ArrowRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <div className="px-1 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Quick Actions
        </div>
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action) => {
            const Icon = getIcon(action.icon)
            return (
              <button
                key={action.id}
                onClick={() => {
                  if (action.url) window.location.href = action.url
                  else if (action.action) action.action()
                  onClose()
                }}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  'bg-muted/60 hover:bg-primary/10',
                  'border border-border hover:border-primary/40',
                  'text-muted-foreground hover:text-primary'
                )}
              >
                <Icon className="w-4 h-4" />
                {action.label}
                {action.shortcut && (
                  <kbd className="ml-1 px-1.5 py-0.5 text-[9px] font-mono bg-background text-muted-foreground rounded border border-border">
                    {action.shortcut}
                  </kbd>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
